import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "motion/react";

/** Minimum characters revealed per frame — keeps a slow trickle (a token or
 *  two per rAF-batched frame) still reading as a steady typewriter instead of
 *  stalling on a 1-char-per-frame crawl. */
const MIN_STEP_CHARS = 2;

/** Fraction of the remaining gap (target.length - revealed) closed per frame.
 *  Geometric catch-up: a burst of ~200 chars (a typical rAF-coalesced chunk,
 *  see useStreamingTurn's batching) closes in ~25 frames (~400ms @60fps),
 *  fast enough that the animation never visibly lags the real stream, while
 *  still reading as progressive reveal rather than an instant paste. */
const CATCHUP_RATE = 0.2;

/**
 * Smooths a fast/bursty streaming string into a progressive "typewriter"
 * reveal, layered on top of useStreamingTurn's per-rAF frame batching (which
 * is a perf necessity, not a UX one — it's why the raw accumulated text can
 * jump by a whole paragraph in one render). This hook doesn't change what
 * arrives or when; it just reveals a growing prefix of `target` at a capped,
 * catch-up rate so the jump *reads* as smooth streaming instead of a chunky
 * paste.
 *
 * - While `active`, `revealed` advances toward `target.length` once per
 *   animation frame, geometrically catching up (see CATCHUP_RATE) so it never
 *   lags far behind a burst. Each step snaps to a word boundary so the reveal
 *   never stops mid-word — markdown tokens that hug their word ("**bold**",
 *   "`code`", "[text](url)") reveal atomically instead of flashing half-parsed.
 * - When `active` is false (stream ended / this is history) or the user
 *   prefers reduced motion, `target` is returned in full immediately — no
 *   animation, no replay.
 * - If `target` gets shorter than `revealed` (new turn, or a narration span
 *   being stripped mid-stream — see stripNarrationSpan in useStreamingTurn),
 *   `revealed` is clamped down to `target.length` rather than animating
 *   backwards.
 */
export function useSmoothedText(target: string, active: boolean): string {
  const reduce = useReducedMotion() ?? false;
  const snap = !active || reduce;

  const [revealed, setRevealed] = useState(() => (snap ? target.length : 0));
  const revealedRef = useRef(revealed);
  revealedRef.current = revealed;
  const targetRef = useRef(target);
  targetRef.current = target;
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (snap) {
      // Stream ended or reduced-motion: show everything, right now.
      setRevealed(target.length);
      return;
    }
    // New turn / reset / a narration strip shrank the trailing text part —
    // never animate backwards or replay, just re-anchor.
    if (target.length < revealedRef.current) {
      setRevealed(target.length);
    }

    const scheduleFrame = (cb: () => void): number =>
      typeof requestAnimationFrame === "function"
        ? requestAnimationFrame(cb)
        : (setTimeout(cb, 16) as unknown as number);

    const step = () => {
      rafRef.current = null;
      const t = targetRef.current;
      const len = t.length;
      const cur = revealedRef.current;
      if (cur >= len) return; // settled — re-armed by the next effect run
      const advance = Math.max(MIN_STEP_CHARS, Math.ceil((len - cur) * CATCHUP_RATE));
      let next = Math.min(len, cur + advance);
      // Snap to a word boundary so we never stop mid-word: a markdown token
      // that hugs its word ("**bold**", "`code`", "[text](url)") then reveals
      // atomically instead of flashing half-parsed (e.g. "**bol"). Walk through
      // the rest of the current word, then the trailing whitespace, landing at
      // the start of the next word. If the (still-growing) buffer ends first,
      // reveal to there and let the next target update carry on.
      if (next < len) {
        while (next < len && !/\s/.test(t[next]!)) next++;
        while (next < len && /\s/.test(t[next]!)) next++;
      }
      setRevealed(next);
      if (next < len) rafRef.current = scheduleFrame(step);
    };
    rafRef.current = scheduleFrame(step);

    return () => {
      if (rafRef.current == null) return;
      if (typeof cancelAnimationFrame === "function") cancelAnimationFrame(rafRef.current);
      else clearTimeout(rafRef.current as unknown as ReturnType<typeof setTimeout>);
      rafRef.current = null;
    };
    // Re-run on every target change: each rAF-batched render delivers at most
    // one text update, so this restarts the loop in step with the real frame
    // rate rather than free-running via self-recursion alone.
  }, [target, snap]);

  return target.slice(0, snap ? target.length : revealed);
}
